import { Icon, type AppIconName } from '../../icons';
import {
  Button as ButtonPrimitive,
  Slottable,
  Tooltip,
  TooltipTrigger,
  type ButtonProps as ButtonPrimitiveProps,
} from '../../primitives';
import { cn, cva, type VariantProps } from '../../shared';
import { TooltipContent, type TooltipContentProps } from '../overlay/tooltip';

export const buttonVariants = cva(
  // data-disabled rather than :disabled — the primitive sets it on both the
  // button and the element asChild renders, and an anchor is never :disabled.
  'inline-flex select-none items-center justify-center gap-inline rounded-md focus-ring transition-colors duration-fast data-disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-active',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary-hover active:bg-secondary-active',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive-hover active:bg-destructive-active',
        outline:
          'border border-surface-border bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground',
        ghost: 'bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground',
        link: 'bg-transparent text-primary underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-control-sm px-button text-caption',
        md: 'h-control px-button text-body',
        lg: 'h-control-lg px-button text-title',
        // A glyph is sized in em, so every size must set one — including this
        // one, or an icon-only button inherits a font size from wherever.
        icon: 'size-control text-body',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

type ButtonVariants = VariantProps<typeof buttonVariants>;

interface ButtonBaseProps extends ButtonPrimitiveProps {
  variant?: NonNullable<ButtonVariants['variant']>;
  /**
   * Occupies the leading slot while `loading`, in place of the icon that would
   * be there — so the button keeps its width when it starts loading rather than
   * growing by a glyph. `rightIcon` is unaffected and stays visible.
   */
  loadingIcon?: AppIconName;
  /**
   * Renders the button inside a tooltip carrying this text. Needs a
   * `TooltipProvider` ancestor, as any tooltip does.
   *
   * On a button with no visible label this doubles as the accessible name, so
   * the name and the tooltip can never drift apart. When the two are the same
   * string the description is dropped — otherwise a screen reader reads it
   * twice, once as the name and once as the description.
   */
  tooltip?: string;
  tooltipSide?: NonNullable<TooltipContentProps['side']>;
}

export interface LabelledButtonProps extends ButtonBaseProps {
  size?: Exclude<NonNullable<ButtonVariants['size']>, 'icon'>;
  /**
   * Taken as a registry name rather than a node, so the button owns the parts a
   * call site keeps getting wrong: the glyph is hidden from assistive
   * technology, it sits inside the flex row at the right gap, and it is sized by
   * the button rather than by whatever font size happened to cascade.
   */
  leftIcon?: AppIconName;
  rightIcon?: AppIconName;
  icon?: never;
}

interface IconOnlyBase extends ButtonBaseProps {
  size: 'icon';
  icon: AppIconName;
  leftIcon?: never;
  rightIcon?: never;
}

/**
 * The glyph is hidden from assistive technology, so one of `aria-label` or
 * `tooltip` is required — without either the button has no accessible name.
 */
export type IconOnlyButtonProps =
  (IconOnlyBase & { 'aria-label': string }) | (IconOnlyBase & { tooltip: string });

export type ButtonProps = LabelledButtonProps | IconOnlyButtonProps;

export function Button({
  variant,
  size,
  className,
  loading = false,
  asChild = false,
  icon,
  leftIcon,
  rightIcon,
  loadingIcon = 'spinner',
  tooltip,
  tooltipSide = 'top',
  children,
  ...props
}: ButtonProps) {
  const lead = icon ?? leftIcon;
  const label = props['aria-label'] ?? (size === 'icon' ? tooltip : undefined);
  const namesItself = tooltip !== undefined && label === tooltip;

  const control = (
    <ButtonPrimitive
      className={cn(buttonVariants({ variant, size }), className)}
      loading={loading}
      asChild={asChild}
      aria-label={label}
      {...(namesItself ? { 'aria-describedby': undefined } : null)}
      {...props}
    >
      {loading ? (
        <Icon name={loadingIcon} aria-hidden="true" className="animate-spin" />
      ) : lead ? (
        <Icon name={lead} aria-hidden="true" />
      ) : null}
      {/* Slot scans these children for the Slottable to find the element it
          merges onto. A fragment around them hides it and the icons vanish. */}
      {asChild ? <Slottable>{children}</Slottable> : children}
      {rightIcon ? <Icon name={rightIcon} aria-hidden="true" /> : null}
    </ButtonPrimitive>
  );

  if (tooltip === undefined) return control;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{control}</TooltipTrigger>
      <TooltipContent side={tooltipSide}>{tooltip}</TooltipContent>
    </Tooltip>
  );
}
